var _dec, _dec2, _class, _SideNavBar;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { Component, Children, isValidElement } from 'react';
import { testable } from '@instructure/ui-testable';
import { omitProps, safeCloneElement } from '@instructure/ui-react-utils';
import { IconMoveStartLine } from '@instructure/ui-icons';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { withStyle } from '@instructure/emotion';
import { SideNavBarItem } from './SideNavBarItem';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const navMinimized = ({
  minimized
}) => ({
  minimized: !minimized
});

/**
---
category: components
---
**/
let SideNavBar = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_SideNavBar = class SideNavBar extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleNavToggle = event => {
      if (!this.isControlled()) {
        this.setState(navMinimized);
      }
      if (typeof this.props.onMinimized === 'function') {
        this.props.onMinimized(event, !this.minimized);
      }
    };
    this.state = {
      minimized: this.isControlled(props) ? !!props.minimized : !!props.defaultMinimized
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, {
      minimized: this.minimized
    });
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, {
      minimized: this.minimized
    });
  }
  get minimized() {
    if (this.isControlled()) {
      return !!this.props.minimized;
    }
    return !!this.state.minimized;
  }
  isControlled(props = this.props) {
    return typeof props.minimized === 'boolean';
  }
  renderChildren() {
    return Children.map(this.props.children, child => {
      var _this$props$styles;
      if (! /*#__PURE__*/isValidElement(child)) return null;
      const navItem = safeCloneElement(child, {
        minimized: this.state.minimized
      });
      return _jsx("li", {
        css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.list,
        children: navItem
      });
    });
  }
  toggleMessage() {
    return this.state.minimized ? this.props.toggleLabel.minimizedLabel : this.props.toggleLabel.expandedLabel;
  }
  render() {
    var _this$props$styles2, _this$props$styles3, _this$props$styles4;
    const label = this.props.label;
    const props = omitProps(this.props, SideNavBar.allowedProps, ['minimized']);
    return _jsxs("nav", {
      ...props,
      css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.navigation,
      "aria-label": label,
      ref: element => {
        this.ref = element;
      },
      children: [_jsx("ul", {
        css: (_this$props$styles3 = this.props.styles) === null || _this$props$styles3 === void 0 ? void 0 : _this$props$styles3.content,
        children: this.renderChildren()
      }), _jsx(SideNavBarItem, {
        "aria-expanded": !this.minimized,
        onClick: this.handleNavToggle,
        icon: _jsx(IconMoveStartLine, {
          css: (_this$props$styles4 = this.props.styles) === null || _this$props$styles4 === void 0 ? void 0 : _this$props$styles4.toggleIcon,
          inline: false
        }),
        label: _jsx(ScreenReaderContent, {
          children: this.toggleMessage()
        })
      })]
    });
  }
}, _SideNavBar.displayName = "SideNavBar", _SideNavBar.componentId = 'SideNavBar', _SideNavBar.allowedProps = allowedProps, _SideNavBar.propTypes = propTypes, _SideNavBar.defaultProps = {
  children: null,
  defaultMinimized: false,
  // TODO we should investigate later if it used or not
  onClick: function (_e) {}
}, _SideNavBar.Item = SideNavBarItem, _SideNavBar)) || _class) || _class);
export default SideNavBar;
export { SideNavBar, SideNavBarItem };